import { useMsal } from '@azure/msal-react';
import * as React from 'react';
import { createContext, useContext } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';

import { changeTenant } from './helpers';
import { useActualizeLoadingStatus, useFetchEmailOnInit, useLoadInitialItemsOnLogin, useLogin, useLogout, useRemoveCandidateTokenFromUrl } from './hooks';
import { initialState } from './store/authSlice';
import { AuthStatus, AvailableTenant, CandidateAccessTokenConfig, User } from './types';
import AuthFailedDialog from './ui/AuthFailedDialog';

export interface AuthContextShape {
    /** Is authenticated using jwt (not static token) */
    isAuthenticated: boolean;
    user: User | null;
    availableTenants: AvailableTenant[];
    login: (redirectStartPage?: string) => Promise<void>;
    logout: () => void;
    changeTenant: (tenant: AvailableTenant) => void;
    accessToken: CandidateAccessTokenConfig;
    status: AuthStatus;
    currentCandidateEmail: string | null;
}

export const initialValue: AuthContextShape = {
    status: 'idle',
    isAuthenticated: false,
    user: null,
    availableTenants: [],
    login: Promise.resolve,
    logout: () => { /* no-op */ },
    changeTenant: () => { /* no-op */ },
    accessToken: initialState.accessToken,
    currentCandidateEmail: null
};

export const AuthContext = createContext<AuthContextShape>(initialValue);

export const AuthProvider: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
    const { instance, inProgress } = useMsal();

    const user = useSelector((state: RootState) => state.auth.user);
    const currentCandidateEmail = useSelector((state: RootState) => state.auth.currentCandidateEmail);
    const status = useSelector((state: RootState) => state.auth.status);
    const accessTokenConfig = useSelector((state: RootState) => state.auth.accessToken);
    const availableTenants = useSelector((state: RootState) => state.auth.availableTenants);

    const logout = useLogout(instance);
    const login = useLogin(instance);

    useRemoveCandidateTokenFromUrl();
    useActualizeLoadingStatus(instance, inProgress);
    useFetchEmailOnInit();
    useLoadInitialItemsOnLogin(instance, inProgress);

    return (
        <AuthContext.Provider value={{
            isAuthenticated: status === 'authenticated' ||
                             status === 'pendingSilent' && accessTokenConfig.fullAccess,
            user,
            availableTenants,
            login,
            logout,
            accessToken: accessTokenConfig,
            status,
            changeTenant,
            currentCandidateEmail
        }}>
            { children }
            { status === 'unauthorized' && <AuthFailedDialog /> }
        </AuthContext.Provider>
    );
};

export function useCandidateAuth(): AuthContextShape {
    return useContext(AuthContext);
}
